/**
 * #2919 — the app-update coordinator. Pure module state, no React.
 *
 * The service worker is registered in `registerType: "prompt"` mode
 * (vite.config.ts): a new build installs and then WAITS, so the old
 * precache (and the old page's lazy chunks) stay served until the swap.
 * `main.tsx` wires the plugin's callbacks to this module:
 *
 *   - `startAppUpdateChecks` asks the browser for a new worker every 60 s,
 *     when the tab becomes visible and when the browser comes back online.
 *     A long-open tab or installed app otherwise never checks again.
 *   - `markUpdateReady` (plugin `onNeedRefresh`) records the plugin's
 *     `updateSW` once a new worker is waiting. The strip
 *     (`components/AppUpdateStrip.tsx`) shows from then on.
 *   - The update is applied at a safe moment: at once when the tab is
 *     hidden, on the next in-app page change (`noteRouteChange`), or when
 *     the user clicks the strip. Never while a text field has focus: a
 *     reload would drop what the user is typing.
 *   - `handleNeedReload` (plugin `onNeedReload`) runs in EVERY open tab
 *     when the new worker takes control (`clientsClaim`), not only in the
 *     tab that applied it. The tab that applied reloads at once. Any other
 *     tab reloads at its next safe moment: at once when nobody types,
 *     else on the next page change, when the tab is hidden, or on the
 *     strip's Reload. Not on focus loss: focus leaves a field on
 *     mouse-down, before the click on Save lands, so a reload there
 *     would drop the save. Until then it runs old code under the new
 *     precache; a lazy chunk it misses goes through `handlePreloadError`.
 *   - `handlePreloadError` handles Vite's `vite:preloadError` (a lazy
 *     chunk that 404s because its deployment is gone): one reload, guarded
 *     in sessionStorage so two failures within 60 s do not loop.
 *
 * Applying means `updateSW(true)`: the plugin posts SKIP_WAITING to the
 * waiting worker. The page reloads when the new worker takes control
 * (`onNeedReload`, or `controllerchange` on a first visit, where the
 * plugin reports no update). A 5 s fallback reload covers a swap that
 * never lands.
 */

export type ApplyUpdate = (reloadPage?: boolean) => Promise<void>;

export const UPDATE_CHECK_INTERVAL_MS = 60_000;
export const PRELOAD_RELOAD_KEY = "tarii.preload_reload_at";
const PRELOAD_GUARD_MS = 60_000;
const FALLBACK_RELOAD_MS = 5_000;

let pendingApply: ApplyUpdate | null = null;
let applying = false;
let reloadPending = false;
let reloading = false;
let fallbackTimer: number | null = null;
let controllerListener: (() => void) | null = null;
let reloadPage: () => void = defaultReload;
let reloadingTimer: number | null = null;
let hiddenListenerAttached = false;
const listeners = new Set<() => void>();

// A page still alive this long after a reload had its reload cancelled
// (a `beforeunload` prompt the user answered with Stay).
const RELOAD_CANCELLED_MS = 3_000;

function defaultReload(): void {
  window.location.reload();
}

/**
 * Reload once; repeat calls while a reload is under way are no-ops. If
 * the page is still here after 3 s the reload was cancelled, so later
 * Reload clicks and page-change applies work again.
 */
function doReload(): void {
  if (reloading) return;
  reloading = true;
  reloadPage();
  reloadingTimer = window.setTimeout(() => {
    reloading = false;
    reloadingTimer = null;
  }, RELOAD_CANCELLED_MS);
}

function notify(): void {
  for (const listener of listeners) listener();
}

/** True once a new build is waiting, or has taken over from another tab. */
export function isUpdateReady(): boolean {
  return pendingApply !== null || reloadPending;
}

/** Subscribe to "update ready" changes (for `useSyncExternalStore`). */
export function subscribeAppUpdate(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True when a text field or editable region has focus. */
export function isUserTyping(doc: Document = document): boolean {
  const el = doc.activeElement;
  if (!el || el === doc.body) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  return el.getAttribute("contenteditable") === "true";
}

function onVisibilityChange(): void {
  if (document.visibilityState === "hidden") applyUpdateWhenSafe();
}

function attachHiddenListener(): void {
  if (hiddenListenerAttached || typeof document === "undefined") return;
  document.addEventListener("visibilitychange", onVisibilityChange);
  hiddenListenerAttached = true;
}

/** A new worker is waiting; keep the plugin's `updateSW` to apply it. */
export function markUpdateReady(apply: ApplyUpdate): void {
  pendingApply = apply;
  attachHiddenListener();
  notify();
  if (document.visibilityState === "hidden") applyUpdateWhenSafe();
}

/**
 * The new worker took control of this page (plugin `onNeedReload`). In
 * the tab that applied the update, reload at once. In any other tab,
 * reload at the next safe moment, never while the user types.
 */
export function handleNeedReload(): void {
  if (applying) {
    doReload();
    return;
  }
  reloadPending = true;
  attachHiddenListener();
  notify();
  applyUpdateWhenSafe();
}

/**
 * Apply the waiting update now (the strip's Reload button). Without a
 * waiting update, or once another tab swapped the worker, this is a
 * plain reload.
 */
export function applyUpdate(): void {
  const apply = pendingApply;
  if (reloadPending || !apply) {
    doReload();
    return;
  }
  if (applying) return;
  applying = true;
  const sw = typeof navigator !== "undefined" ? navigator.serviceWorker : undefined;
  if (sw && !controllerListener) {
    controllerListener = () => doReload();
    sw.addEventListener("controllerchange", controllerListener, { once: true });
  }
  void apply(true).catch(() => {
    // The swap failed to start; the fallback below still reloads.
  });
  fallbackTimer = window.setTimeout(() => doReload(), FALLBACK_RELOAD_MS);
}

/** Apply (or reload) when an update is ready and nobody is typing. */
export function applyUpdateWhenSafe(): boolean {
  if (!isUpdateReady() || isUserTyping()) return false;
  applyUpdate();
  return true;
}

/** The in-app page changed: a safe moment to apply a waiting update. */
export function noteRouteChange(): void {
  applyUpdateWhenSafe();
}

/**
 * Ask the browser for a new worker. Skipped offline, while a worker is
 * already installing, and when the worker script does not answer 200
 * (the recommended vite-plugin-pwa precheck: a failed `update()` throws).
 */
export async function checkForUpdate(
  swUrl: string,
  registration: ServiceWorkerRegistration,
): Promise<void> {
  if (registration.installing) return;
  if (typeof navigator !== "undefined" && "onLine" in navigator && !navigator.onLine) return;
  try {
    const resp = await fetch(swUrl, {
      cache: "no-store",
      headers: { cache: "no-store", "cache-control": "no-cache" },
    });
    if (resp.status === 200) await registration.update();
  } catch {
    // Network blip: the next check tries again.
  }
}

/** Start the periodic, visibility and online checks. Returns a stop function. */
export function startAppUpdateChecks(
  swUrl: string,
  registration: ServiceWorkerRegistration | undefined,
): () => void {
  if (!registration) return () => {};
  const check = () => {
    void checkForUpdate(swUrl, registration);
  };
  const onVisible = () => {
    if (document.visibilityState === "visible") check();
  };
  const timer = window.setInterval(check, UPDATE_CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("online", check);
  attachHiddenListener();
  return () => {
    window.clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("online", check);
  };
}

function readStorage(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked: the reload still happens once per page load.
  }
}

/**
 * `vite:preloadError`: a lazy chunk failed to load (its deployment is
 * gone). Reload at once and swallow the error. After `preventDefault`
 * the import resolves to nothing, so a plain reload (not the waiting
 * update and its swap) keeps the error screen off. A second failure
 * within 60 s is left to surface, so a broken build never loops.
 */
export function handlePreloadError(event: Event): void {
  const now = Date.now();
  const last = Number(readStorage(PRELOAD_RELOAD_KEY) ?? 0);
  if (last > 0 && now - last < PRELOAD_GUARD_MS) return;
  event.preventDefault();
  writeStorage(PRELOAD_RELOAD_KEY, String(now));
  doReload();
}

/** Test seam: reset module state and swap the page reload. */
export function __resetAppUpdateForTests(opts?: { reload?: () => void }): void {
  pendingApply = null;
  applying = false;
  reloadPending = false;
  reloading = false;
  if (reloadingTimer !== null) {
    window.clearTimeout(reloadingTimer);
    reloadingTimer = null;
  }
  if (fallbackTimer !== null) {
    window.clearTimeout(fallbackTimer);
    fallbackTimer = null;
  }
  if (controllerListener) {
    navigator.serviceWorker?.removeEventListener("controllerchange", controllerListener);
    controllerListener = null;
  }
  reloadPage = opts?.reload ?? defaultReload;
  if (hiddenListenerAttached) {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    hiddenListenerAttached = false;
  }
  notify();
}
